import React, {FC, Fragment, useState} from 'react'
import {Button, Modal, Spinner} from 'react-bootstrap'
import {toast} from 'react-toastify'
import {PlatformDataType} from 'src/interfaces/Platform.interface'
import {uploadAsync} from 'src/redux/actions/uploadAction'
import {useAppDispatch} from 'src/redux/storeHooks'
import {isApiPayloadSuccess} from 'src/marketConnect/helpers'

const ALLOWED_EXTENSIONS = ['csv', 'xls', 'xlsx', 'tab']
const UPLOAD_FEATURE = 'IMPORT_PRODUCTS_ERROR'

type Props = {
  show: boolean
  handleClose: () => void
  platformId: string
  platformData: PlatformDataType | null
}

const ManoManoErrorReportUploadModal: FC<Props> = ({
  show,
  handleClose,
  platformId,
  platformData,
}) => {
  const dispatch = useAppDispatch()
  const [csvFile, setCSVFile] = useState<File | null>(null)
  const [loading, setLoading] = useState(false)
  const [invalidFileType, setInvalidFileType] = useState(false)

  const resetForm = () => {
    setCSVFile(null)
    setInvalidFileType(false)
    setLoading(false)
  }

  const handleCloseModal = () => {
    resetForm()
    handleClose()
  }

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0]
    if (!selectedFile) return

    const fileExtension = selectedFile.name.split('.').pop()?.toLowerCase()
    if (!fileExtension || !ALLOWED_EXTENSIONS.includes(fileExtension)) {
      setInvalidFileType(true)
      setCSVFile(null)
      event.target.value = ''
      return
    }

    setCSVFile(selectedFile)
    setInvalidFileType(false)
  }

  const handleUploadClick = async () => {
    if (!(csvFile instanceof File)) {
      toast.warning('Please choose a file to upload')
      return
    }

    setLoading(true)
    try {
      const result = await dispatch(
        uploadAsync({
          id: platformId,
          csvFile,
          type: UPLOAD_FEATURE,
        })
      )

      if (!isApiPayloadSuccess(result)) {
        return
      }

      const message =
        (result as {payload?: {success?: string; message?: string}})?.payload?.success ||
        (result as {payload?: {success?: string; message?: string}})?.payload?.message ||
        'Error report uploaded successfully'

      toast.success(message)
      handleCloseModal()
    } catch {
      // API errors are surfaced by the global dispatch wrapper.
    } finally {
      setLoading(false)
    }
  }

  return (
    <Fragment>
      <Modal show={show} onHide={handleCloseModal}>
        <Modal.Header closeButton>
          <Modal.Title>Upload Error Report</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className='text-muted mb-4'>
            Upload the exported failure report from your Seller Portal. We will match the SKUs to
            update your sync logs and help fix the errors
            {platformData?.display_name ? ` for ${platformData.display_name}` : ''}.
          </p>
          <input
            key='manomano-error-report-upload'
            type='file'
            className='form-control'
            accept='.csv,.xls,.xlsx,.tab'
            onChange={handleFileChange}
          />
          <small
            className={`form-text ${invalidFileType ? 'text-danger' : 'text-muted'}`}
            style={{marginLeft: '7rem'}}
          >
            Please upload a file in .csv, .xls, .xlsx or .tab format.
          </small>
        </Modal.Body>
        <Modal.Footer>
          <Button disabled={loading} variant='secondary' onClick={handleCloseModal}>
            Close
          </Button>
          <Button disabled={loading || csvFile == null} variant='primary' onClick={handleUploadClick}>
            {loading ? (
              <>
                <Spinner animation='border' size='sm' className='me-2' />
                Uploading...
              </>
            ) : (
              'Upload'
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </Fragment>
  )
}

export default ManoManoErrorReportUploadModal
